// Client-side port of /goalies/rows. Mirrors top_goalies in
// src/pwhlstats/web/goalie_leaderboard.py — same SQL, same parameter
// order, same sort keys. Renders into the page's #g-results container
// matching templates/_goalies_rows.html so the existing CSS works.

import { getDbWorker } from "./db";
import { STRENGTH_STATES, strengthPredForGoalie } from "./filters";

const MIN_SF_CHOICES = [50, 100, 200, 400];
export const SORT_KEYS: Record<string, string> = {
  gsax: "gsax DESC",
  save_pct: "save_pct DESC",
  hd_save_pct: "hd_save_pct DESC",
  clutch_save_pct: "clutch_save_pct DESC",
  shots_faced: "shots_faced DESC",
  xga: "xga DESC",
  ga: "ga ASC",
  name: "name COLLATE NOCASE ASC",
};

const CLUTCH_XG = 0.2;

const STRENGTH_LABELS: Record<string, string> = {
  "": "any strength",
  "5v5": "5v5",
  pp: "PP",
  pk: "PK",
  "4v4": "4v4",
  "3v3": "3v3",
  en: "empty net",
};

interface GoalieRow {
  player_id: number;
  name: string;
  team_code: string | null;
  shots_faced: number;
  saves: number;
  ga: number;
  xga: number;
  gsax: number;
  save_pct: number | null;
  hd_sf: number;
  hd_saves: number;
  hd_ga: number;
  hd_save_pct: number | null;
  clutch_sf: number;
  clutch_saves: number;
  clutch_save_pct: number | null;
  perim_sf: number;
  perim_saves: number;
  perim_save_pct: number | null;
  point_sf: number;
  point_saves: number;
  point_save_pct: number | null;
}

interface GoalieFilters {
  strength_state: string;
  team_id: number | null;
  min_sf: number;
  sort_by: string;
  season_type: string | null;
}

function buildSql(filters: GoalieFilters): { sql: string; params: (number | string)[] } {
  const params: (number | string)[] = [];
  const ss = filters.strength_state;
  const strengthPred =
    ss && ss in STRENGTH_STATES
      ? `AND ${strengthPredForGoalie(ss) ?? ""}`.replace(/AND $/, "")
      : "";
  let seasonPred = "";
  if (filters.season_type === "regular") {
    seasonPred = "AND (g.season_type = 'regular' OR g.season_type IS NULL)";
  } else if (
    filters.season_type === "playoff" ||
    filters.season_type === "preseason"
  ) {
    seasonPred = `AND g.season_type = '${filters.season_type}'`;
  }
  let teamFilterSql = "";
  if (filters.team_id !== null) {
    teamFilterSql =
      "AND ((e.team_id = g.home_team_id AND g.away_team_id = ?) OR " +
      "     (e.team_id = g.away_team_id AND g.home_team_id = ?))";
    params.push(filters.team_id, filters.team_id);
  }
  params.push(filters.min_sf);
  const orderSql = SORT_KEYS[filters.sort_by] ?? SORT_KEYS["gsax"]!;
  params.push(100);

  const sql = `
    WITH game_team_goalie AS (
        SELECT game_id, shooter_team_id, goalie_id
        FROM (
            SELECT e.game_id, e.team_id AS shooter_team_id, e.goalie_id,
                   COUNT(*) AS cnt,
                   ROW_NUMBER() OVER (
                       PARTITION BY e.game_id, e.team_id
                       ORDER BY COUNT(*) DESC
                   ) AS rn
            FROM events e
            WHERE e.event_type = 'shot'
              AND e.goalie_id IS NOT NULL AND e.goalie_id != 0
            GROUP BY e.game_id, e.team_id, e.goalie_id
        ) ranked
        WHERE rn = 1
    ),
    faced AS (
        SELECT
            COALESCE(e.goalie_id, ggt.goalie_id) AS player_id,
            e.is_goal,
            COALESCE(e.xg, 0) AS xg,
            e.zone,
            CASE WHEN e.team_id = g.home_team_id THEN g.away_team_id
                 ELSE g.home_team_id END AS goalie_team_id
        FROM events e
        JOIN games g ON g.game_id = e.game_id
        LEFT JOIN game_team_goalie ggt
            ON ggt.game_id = e.game_id AND ggt.shooter_team_id = e.team_id
        WHERE e.event_type IN ('shot','goal')
          AND COALESCE(e.goalie_id, ggt.goalie_id) IS NOT NULL
          AND COALESCE(e.goalie_id, ggt.goalie_id) != 0
          ${strengthPred}
          ${seasonPred}
          ${teamFilterSql}
    ),
    agg AS (
        SELECT
            player_id,
            COUNT(*) AS shots_faced,
            SUM(CASE WHEN is_goal = 1 THEN 1 ELSE 0 END) AS ga,
            SUM(CASE WHEN is_goal = 0 THEN 1 ELSE 0 END) AS saves,
            SUM(xg) AS xga,
            SUM(CASE WHEN zone = 'slot' THEN 1 ELSE 0 END) AS hd_sf,
            SUM(CASE WHEN zone = 'slot' AND is_goal = 0 THEN 1 ELSE 0 END) AS hd_saves,
            SUM(CASE WHEN zone = 'slot' AND is_goal = 1 THEN 1 ELSE 0 END) AS hd_ga,
            SUM(CASE WHEN xg >= ${CLUTCH_XG} THEN 1 ELSE 0 END) AS clutch_sf,
            SUM(CASE WHEN xg >= ${CLUTCH_XG} AND is_goal = 0 THEN 1 ELSE 0 END) AS clutch_saves,
            SUM(CASE WHEN zone = 'perimeter' THEN 1 ELSE 0 END) AS perim_sf,
            SUM(CASE WHEN zone = 'perimeter' AND is_goal = 0 THEN 1 ELSE 0 END) AS perim_saves,
            SUM(CASE WHEN zone = 'point' THEN 1 ELSE 0 END) AS point_sf,
            SUM(CASE WHEN zone = 'point' AND is_goal = 0 THEN 1 ELSE 0 END) AS point_saves,
            (SELECT goalie_team_id FROM faced f2
             WHERE f2.player_id = faced.player_id AND f2.goalie_team_id IS NOT NULL
             GROUP BY goalie_team_id ORDER BY COUNT(*) DESC LIMIT 1) AS team_id
        FROM faced
        GROUP BY player_id
        HAVING shots_faced >= ?
    )
    SELECT
        a.player_id,
        p.first_name || ' ' || p.last_name AS name,
        t.code AS team_code,
        a.shots_faced, a.saves, a.ga, a.xga,
        (a.xga - a.ga) AS gsax,
        CAST(a.saves AS REAL) / NULLIF(a.shots_faced, 0) AS save_pct,
        a.hd_sf, a.hd_saves, a.hd_ga,
        CAST(a.hd_saves AS REAL) / NULLIF(a.hd_sf, 0) AS hd_save_pct,
        a.clutch_sf, a.clutch_saves,
        CAST(a.clutch_saves AS REAL) / NULLIF(a.clutch_sf, 0) AS clutch_save_pct,
        a.perim_sf, a.perim_saves,
        CAST(a.perim_saves AS REAL) / NULLIF(a.perim_sf, 0) AS perim_save_pct,
        a.point_sf, a.point_saves,
        CAST(a.point_saves AS REAL) / NULLIF(a.point_sf, 0) AS point_save_pct
    FROM agg a
    JOIN players p ON p.player_id = a.player_id
    LEFT JOIN teams t ON t.team_id = a.team_id
    ORDER BY ${orderSql}
    LIMIT ?
  `;
  return { sql, params };
}

function escapeHtml(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Short stat definition for a glossary slug, from the shared global injected
// by base.html (source: stats_glossary.py). Empty string when unknown.
function glossShort(slug: string): string {
  const g = (window as unknown as { __PWHL_GLOSS__?: Record<string, string> }).__PWHL_GLOSS__;
  return (g && g[slug]) || "";
}

// Plain (non-sortable) header cell carrying a glossary tooltip.
function glossTh(label: string, gkey: string): string {
  const tip = glossShort(gkey);
  if (!tip) return `<th class="num">${escapeHtml(label)}</th>`;
  return `<th class="num gloss-th" data-glossary="${escapeHtml(tip)}">${escapeHtml(label)}</th>`;
}

function sortHeader(
  label: string,
  key: string,
  current: string,
  align = "num",
  gkey?: string,
): string {
  const active = current === key ? "sort-active" : "";
  const caret = current === key ? " ▾" : "";
  const tip = gkey ? glossShort(gkey) : "";
  const glossClass = tip ? " gloss-th" : "";
  const glossAttr = tip ? ` data-glossary="${escapeHtml(tip)}"` : "";
  return `<th class="${align} sortable${glossClass} ${active}" data-sort-key="${key}"${glossAttr} role="button" tabindex="0">${escapeHtml(label)}${caret}</th>`;
}

function fmtSigned(n: number, decimals = 2): string {
  return `${n >= 0 ? "+" : ""}${n.toFixed(decimals)}`;
}

function fmtPct(value: number | null, denom: number): string {
  if (!denom) return "—";
  return `${((value ?? 0) * 100).toFixed(1)}`;
}

export function renderGoalies(rows: GoalieRow[], filters: GoalieFilters): string {
  const strengthLabel = STRENGTH_LABELS[filters.strength_state] ?? "any strength";
  if (rows.length === 0) {
    return '<p class="empty">No goalies meet those thresholds — try a lower "Min shots faced".</p>';
  }
  const head = `
    <p class="result-meta">
      ${rows.length} goalies (${escapeHtml(strengthLabel)}) — ≥ ${filters.min_sf} shots faced.
      Click a goalie to see every shot they faced in the Play Index.
      <span class="note">High-danger = slot zone. Clutch = saves on shots with xG ≥ 0.20.</span>
    </p>
    <table class="results leaderboard standalone">
      <thead>
        <tr>
          <th class="rank" rowspan="2">#</th>
          <th class="text" rowspan="2">Goalie</th>
          <th class="text" rowspan="2">Team</th>
          <th class="num" colspan="6" style="border-bottom: 1px solid var(--rule, #ccc);">Core</th>
          <th class="num" colspan="3" style="border-bottom: 1px solid var(--rule, #ccc);">High-Danger (slot)</th>
          <th class="num" colspan="3" style="border-bottom: 1px solid var(--rule, #ccc);">Clutch (xG ≥ 0.20)</th>
          <th class="num" colspan="2" style="border-bottom: 1px solid var(--rule, #ccc);">Zone SV%</th>
        </tr>
        <tr>
          ${sortHeader("SF", "shots_faced", filters.sort_by, "num", "sf")}
          ${glossTh("Saves", "sv")}
          ${sortHeader("GA", "ga", filters.sort_by, "num", "ga")}
          ${sortHeader("SV%", "save_pct", filters.sort_by, "num", "sv_pct")}
          ${sortHeader("xGA", "xga", filters.sort_by, "num", "xga")}
          ${sortHeader("GSAx", "gsax", filters.sort_by, "num", "gsax")}
          ${glossTh("HD SF", "hd_sv_pct")}
          ${glossTh("HD Sv", "hd_sv_pct")}
          ${sortHeader("HDSV%", "hd_save_pct", filters.sort_by, "num", "hd_sv_pct")}
          ${glossTh("Cl SF", "clutch_sv_pct")}
          ${glossTh("Cl Sv", "clutch_sv_pct")}
          ${sortHeader("Cl SV%", "clutch_save_pct", filters.sort_by, "num", "clutch_sv_pct")}
          ${glossTh("Perim", "perim_sv_pct")}
          ${glossTh("Point", "point_sv_pct")}
        </tr>
      </thead>
      <tbody>
  `;
  const body = rows
    .map((r, i) => {
      const svPct = ((r.save_pct ?? 0) * 100).toFixed(1);
      const gsaxClass = r.gsax >= 0 ? "pos" : "neg";
      return `
        <tr>
          <td class="rank">${i + 1}</td>
          <td><a class="skater-link" href="/play-index/?goalie_id=${r.player_id}&event_group=shots_on_net">${escapeHtml(r.name)}</a></td>
          <td>${escapeHtml(r.team_code) || "—"}</td>
          <td class="num">${r.shots_faced}</td>
          <td class="num">${r.saves}</td>
          <td class="num">${r.ga}</td>
          <td class="num">${svPct}</td>
          <td class="num">${r.xga.toFixed(2)}</td>
          <td class="num ${gsaxClass}">${fmtSigned(r.gsax)}</td>
          <td class="num">${r.hd_sf}</td>
          <td class="num">${r.hd_saves}</td>
          <td class="num">${fmtPct(r.hd_save_pct, r.hd_sf)}</td>
          <td class="num">${r.clutch_sf}</td>
          <td class="num">${r.clutch_saves}</td>
          <td class="num">${fmtPct(r.clutch_save_pct, r.clutch_sf)}</td>
          <td class="num">${fmtPct(r.perim_save_pct, r.perim_sf)}</td>
          <td class="num">${fmtPct(r.point_save_pct, r.point_sf)}</td>
        </tr>
      `;
    })
    .join("");
  return `${head}${body}</tbody></table>`;
}

export function readGoalieFilters(form: HTMLFormElement): GoalieFilters {
  const val = (name: string): string =>
    (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null)?.value ?? "";
  const intOrNull = (s: string): number | null => {
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };
  const minSfRaw = intOrNull(val("min_sf")) ?? 100;
  const minSf = MIN_SF_CHOICES.includes(minSfRaw) ? minSfRaw : 100;
  const ss = val("strength_state");
  const sortRaw = val("sort_by") || "gsax";
  const sortBy = sortRaw in SORT_KEYS ? sortRaw : "gsax";
  const seasonType = val("season_type");
  return {
    strength_state: ss in STRENGTH_STATES || ss === "" ? ss : "",
    team_id: intOrNull(val("team_id")),
    min_sf: minSf,
    sort_by: sortBy,
    season_type:
      seasonType === "regular" ||
      seasonType === "playoff" ||
      seasonType === "preseason"
        ? seasonType
        : null,
  };
}

async function runAndRender(
  form: HTMLFormElement,
  results: HTMLElement,
  filters: GoalieFilters,
): Promise<void> {
  results.innerHTML = '<p class="empty">Loading…</p>';
  try {
    const worker = await getDbWorker();
    const { sql, params } = buildSql(filters);
    const rows = (await worker.db.query(sql, params)) as GoalieRow[];
    results.innerHTML = renderGoalies(rows, filters);
    wireSortHeaders(form, results);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    results.innerHTML = `<p class="empty">Query failed: ${escapeHtml(msg)}</p>`;
    console.error("[goalies] query failed:", err);
  }
}

function wireSortHeaders(form: HTMLFormElement, results: HTMLElement): void {
  for (const th of results.querySelectorAll<HTMLElement>("th[data-sort-key]")) {
    th.addEventListener("click", () => {
      let hidden = form.querySelector<HTMLInputElement>('input[name="sort_by"]');
      if (!hidden) {
        hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "sort_by";
        form.appendChild(hidden);
      }
      hidden.value = th.dataset["sortKey"]!;
      void runAndRender(form, results, readGoalieFilters(form));
    });
  }
}

export function initGoalies(): boolean {
  const form = document.getElementById("g-filters") as HTMLFormElement | null;
  const results = document.getElementById("g-results");
  if (!form || !results) return false;

  const refilter = (): void => {
    void runAndRender(form, results, readGoalieFilters(form));
  };
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    refilter();
  });
  form.addEventListener("change", (e) => {
    if (e.target instanceof HTMLSelectElement) refilter();
  });
  refilter();
  return true;
}
